import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { tablesService } from "../../services/tables.service";
import { internalOrdersService } from "../../services/internal-orders.service";
import { productsService, Product } from "../../services/products.service";
import {
  Plus,
  Users,
  X,
  Check,
  Trash2,
  ArrowRightLeft,
  ShoppingCart,
  Receipt,
  Utensils,
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { Button, Input, Card, useToast } from "@caruso/ui";

interface Table {
  id: string;
  number: string;
  capacity: number;
  location?: string;
  active: boolean;
}

interface InternalOrder {
  id: string;
  orderNumber: string;
  tableId?: string | null;
  clientId?: string;
  clientName?: string;
  subtotal: number;
  discount: number;
  serviceCharge: number;
  total: number;
  status: string;
  paymentMethod?: string;
  paymentStatus: string;
  notes?: string;
  openedAt: string;
  closedAt?: string;
  table?: Table | null;
  items: OrderItem[];
}

interface OrderItem {
  id: string;
  productId: string;
  productName: string;
  price: number;
  quantity: number;
  subtotal: number;
  notes?: string;
}

export default function ComandasPage() {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [viewMode, setViewMode] = useState<'mesas' | 'comandas'>('mesas');
  const [selectedTable, setSelectedTable] = useState<Table | null>(null);
  const [selectedOrder, setSelectedOrder] = useState<InternalOrder | null>(null);
  const [showCreateOrderModal, setShowCreateOrderModal] = useState(false);
  const [showAddItemModal, setShowAddItemModal] = useState(false);
  const [showCloseOrderModal, setShowCloseOrderModal] = useState(false);
  const [showTransferModal, setShowTransferModal] = useState(false);

  // Form states
  const [clientName, setClientName] = useState("");
  const [selectedProduct, setSelectedProduct] = useState<string>("");
  const [quantity, setQuantity] = useState(1);
  const [itemNotes, setItemNotes] = useState("");
  const [paymentMethod, setPaymentMethod] = useState("dinheiro");
  const [discount, setDiscount] = useState(0);
  const [serviceCharge, setServiceCharge] = useState(0);
  const [transferTableId, setTransferTableId] = useState("");

  // Queries
  const { data: tables } = useQuery({
    queryKey: ["tables"],
    queryFn: () => tablesService.getAll(true),
  });

  const { data: orders } = useQuery({
    queryKey: ["internal-orders"],
    queryFn: () => internalOrdersService.getAll("open"),
  });

  const { data: products } = useQuery({
    queryKey: ["products"],
    queryFn: () => productsService.getAll({ page: 1, limit: 1000 }),
    select: (data) => data.data?.filter((p: Product) => p.active) || [],
  });

  // Mutations
  const createOrderMutation = useMutation({
    mutationFn: (data: { tableId: string; clientName?: string }) =>
      internalOrdersService.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["internal-orders"] });
      setShowCreateOrderModal(false);
      setClientName("");
      toast({
        title: "Comanda criada!",
        variant: "success",
      });
    },
  });

  const addItemMutation = useMutation({
    mutationFn: (data: { orderId: string; productId: string; quantity: number; notes?: string }) =>
      internalOrdersService.addItem(data.orderId, {
        productId: data.productId,
        quantity: data.quantity,
        notes: data.notes,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["internal-orders"] });
      setShowAddItemModal(false);
      setSelectedProduct("");
      setQuantity(1);
      setItemNotes("");
      toast({
        title: "Item adicionado!",
        variant: "success",
      });
    },
  });

  const removeItemMutation = useMutation({
    mutationFn: ({ orderId, itemId }: { orderId: string; itemId: string }) =>
      internalOrdersService.removeItem(orderId, itemId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["internal-orders"] });
      toast({
        title: "Item removido!",
        variant: "success",
      });
    },
  });

  const closeOrderMutation = useMutation({
    mutationFn: ({
      orderId,
      data,
    }: {
      orderId: string;
      data: {
        paymentMethod: string;
        discount: number;
        serviceCharge: number;
      };
    }) => internalOrdersService.closeOrder(orderId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["internal-orders"] });
      setShowCloseOrderModal(false);
      setSelectedOrder(null);
      setDiscount(0);
      setServiceCharge(0);
      toast({
        title: "Comanda fechada!",
        description: "Pagamento registrado com sucesso",
        variant: "success",
      });
    },
  });

  const transferOrderMutation = useMutation({
    mutationFn: ({ orderId, data }: { orderId: string; data: { newTableId: string } }) =>
      internalOrdersService.transferOrder(orderId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["internal-orders"] });
      setShowTransferModal(false);
      setTransferTableId("");
      toast({
        title: "Comanda transferida!",
        variant: "success",
      });
    },
  });

  // Handlers
  const handleCreateOrder = () => {
    if (!selectedTable) return;
    createOrderMutation.mutate({
      tableId: selectedTable.id,
      clientName: clientName || undefined,
    });
  };

  const handleAddItem = () => {
    if (!selectedOrder || !selectedProduct) return;
    addItemMutation.mutate({
      orderId: selectedOrder.id,
      productId: selectedProduct,
      quantity,
      notes: itemNotes || undefined,
    });
  };

  const handleCloseOrder = () => {
    if (!selectedOrder) return;
    closeOrderMutation.mutate({
      orderId: selectedOrder.id,
      data: {
        paymentMethod,
        discount,
        serviceCharge,
      },
    });
  };

  const handleTransferOrder = () => {
    if (!selectedOrder || !transferTableId) return;
    transferOrderMutation.mutate({
      orderId: selectedOrder.id,
      data: { newTableId: transferTableId },
    });
  };

  const getTableOrder = (tableId: string) => {
    return orders?.find((order: InternalOrder) => order.tableId === tableId && order.status === "open");
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row gap-4 sm:items-center sm:justify-between">
        <div>
          <h1 className="text-3xl font-serif text-brand-gold">Comandas</h1>
          <p className="text-brand-ivory/60 mt-1">Gerenciar comandas e pedidos internos</p>
        </div>
        <div className="flex gap-2 bg-brand-ink/50 rounded-lg p-1 border border-brand-gold/20">
          <button
            onClick={() => setViewMode('mesas')}
            className={`px-4 py-2 rounded-lg transition-all ${
              viewMode === 'mesas'
                ? 'bg-brand-gold text-brand-ink font-semibold'
                : 'text-brand-ivory/60 hover:text-brand-gold'
            }`}
          >
            <Utensils className="w-4 h-4 inline mr-2" />
            Mesas
          </button>
          <button
            onClick={() => setViewMode('comandas')}
            className={`px-4 py-2 rounded-lg transition-all ${
              viewMode === 'comandas'
                ? 'bg-brand-gold text-brand-ink font-semibold'
                : 'text-brand-ivory/60 hover:text-brand-gold'
            }`}
          >
            <Receipt className="w-4 h-4 inline mr-2" />
            Comandas
          </button>
        </div>
      </div>

      {/* Tables Grid */}
      {viewMode === 'mesas' && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {tables?.map((table: Table) => {
            const order = getTableOrder(table.id);
            const isOccupied = !!order;

            return (
              <motion.div
                key={table.id}
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                whileHover={{ scale: 1.02 }}
                transition={{ duration: 0.2 }}
                onClick={() => {
                  setSelectedTable(table);
                  if (order) {
                    setSelectedOrder(order);
                  } else {
                    setShowCreateOrderModal(true);
                  }
                }}
                className="cursor-pointer"
              >
                <Card className={`bg-brand-ink/50 transition-all ${
                  isOccupied
                    ? "border-orange-500/40 bg-orange-500/5"
                    : "border-brand-gold/20 hover:border-brand-gold/40"
                }`}>
                  <div className="p-6">
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center gap-3">
                        <div
                          className={`w-12 h-12 rounded-full flex items-center justify-center ${
                            isOccupied ? "bg-orange-500/20" : "bg-brand-gold/20"
                          }`}
                        >
                          <Utensils className={`w-6 h-6 ${isOccupied ? "text-orange-400" : "text-brand-gold"}`} />
                        </div>
                        <div>
                          <h3 className="text-xl font-serif text-brand-gold">Mesa {table.number}</h3>
                          <p className="text-sm text-brand-ivory/60">
                            <Users className="w-3 h-3 inline mr-1" />
                            {table.capacity} pessoas
                          </p>
                        </div>
                      </div>
                    </div>

                    {isOccupied && order ? (
                      <div className="space-y-2">
                        <div className="flex items-center justify-between text-sm">
                          <span className="text-brand-ivory/60">Comanda:</span>
                          <span className="text-orange-400 font-semibold">{order.orderNumber}</span>
                        </div>
                        {order.clientName && (
                          <div className="flex items-center justify-between text-sm">
                            <span className="text-brand-ivory/60">Cliente:</span>
                            <span className="text-brand-ivory font-semibold">{order.clientName}</span>
                          </div>
                        )}
                        <div className="flex items-center justify-between text-sm">
                          <span className="text-brand-ivory/60">Itens:</span>
                          <span className="text-brand-ivory font-semibold">{order.items?.length || 0}</span>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-brand-ivory/60 text-sm">Total:</span>
                          <span className="text-brand-gold font-bold text-lg">
                            R$ {order.subtotal.toFixed(2)}
                          </span>
                        </div>
                      </div>
                    ) : (
                      <div className="text-center py-4">
                        <p className="text-emerald-400 font-semibold">Disponível</p>
                        <p className="text-sm text-brand-ivory/60 mt-1">Clique para abrir comanda</p>
                      </div>
                    )}
                  </div>
                </Card>
              </motion.div>
            );
          })}
        </div>
      )}

      {/* Comandas Grid */}
      {viewMode === 'comandas' && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {orders && orders.length > 0 ? (
            orders.map((order: InternalOrder) => (
              <motion.div
                key={order.id}
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                whileHover={{ scale: 1.02 }}
                transition={{ duration: 0.2 }}
                onClick={() => setSelectedOrder(order)}
                className="cursor-pointer"
              >
                <Card className="bg-brand-ink/50 border-brand-gold/20 hover:border-brand-gold/40 transition-all">
                  <div className="p-6">
                    {/* Header */}
                    <div className="flex items-start justify-between mb-4">
                      <div>
                        <h3 className="text-2xl font-serif text-brand-gold">
                          #{order.orderNumber}
                        </h3>
                        <p className="text-sm text-brand-ivory/60 mt-1">
                          {order.table ? `Mesa ${order.table.number}` : 'Sem mesa'}
                        </p>
                      </div>
                      <div className="w-10 h-10 rounded-full bg-orange-500/20 flex items-center justify-center">
                        <Receipt className="w-5 h-5 text-orange-400" />
                      </div>
                    </div>

                    {/* Cliente */}
                    {order.clientName && (
                      <div className="mb-4 p-3 bg-brand-smoke/30 rounded-lg border border-brand-gold/10">
                        <div className="flex items-center gap-2 text-brand-ivory">
                          <Users className="w-4 h-4 text-brand-gold" />
                          <span className="text-sm font-medium">{order.clientName}</span>
                        </div>
                      </div>
                    )}

                    {/* Info */}
                    <div className="space-y-3 mb-4">
                      <div className="flex items-center justify-between text-brand-ivory/60">
                        <span className="text-sm">Itens:</span>
                        <span className="text-brand-ivory font-semibold">{order.items?.length || 0}</span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-brand-ivory/60 text-sm">Total:</span>
                        <span className="text-brand-gold font-bold text-lg">
                          R$ {order.subtotal.toFixed(2)}
                        </span>
                      </div>
                    </div>

                    {/* Actions */}
                    <div className="flex items-center gap-2 pt-4 border-t border-brand-gold/10">
                      <Button
                        variant="outline"
                        size="sm"
                        className="flex-1"
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedOrder(order);
                          setShowAddItemModal(true);
                        }}
                      >
                        <ShoppingCart className="w-4 h-4 mr-1" />
                        Adicionar
                      </Button>
                      <Button
                        variant="premium"
                        size="sm"
                        className="flex-1"
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedOrder(order);
                          setShowCloseOrderModal(true);
                        }}
                      >
                        <Check className="w-4 h-4 mr-1" />
                        Fechar
                      </Button>
                    </div>
                  </div>
                </Card>
              </motion.div>
            ))
          ) : (
            <Card className="bg-brand-ink/50 border-brand-gold/20 col-span-full">
              <div className="p-12 text-center">
                <div className="w-16 h-16 rounded-full bg-brand-gold/10 flex items-center justify-center mx-auto mb-4">
                  <Receipt className="w-8 h-8 text-brand-gold" />
                </div>
                <h3 className="text-xl font-semibold text-brand-ivory mb-2">
                  Nenhuma comanda aberta
                </h3>
                <p className="text-brand-ivory/60">
                  Faça um check-in ou abra uma comanda em uma mesa
                </p>
              </div>
            </Card>
          )}
        </div>
      )}

      {/* Create Order Modal */}
      <AnimatePresence>
        {showCreateOrderModal && selectedTable && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/60 backdrop-blur-sm z-40"
              onClick={() => setShowCreateOrderModal(false)}
            />
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="fixed inset-0 flex items-center justify-center z-50 p-4 pointer-events-none"
            >
              <Card className="w-full max-w-md bg-brand-ink border-brand-gold/20 pointer-events-auto">
                <div className="p-6">
                  <div className="flex items-center justify-between mb-6">
                    <h2 className="text-2xl font-serif text-brand-gold">Nova Comanda</h2>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => setShowCreateOrderModal(false)}
                    >
                      <X className="w-5 h-5" />
                    </Button>
                  </div>

                  <div className="mb-6">
                    <p className="text-brand-ivory/60">
                      Mesa: <span className="text-brand-gold font-semibold">{selectedTable.number}</span>
                    </p>
                  </div>

                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-brand-ivory/80 mb-2">
                        Nome do Cliente (opcional)
                      </label>
                      <Input
                        type="text"
                        value={clientName}
                        onChange={(e) => setClientName(e.target.value)}
                        placeholder="Digite o nome..."
                        className="bg-brand-ink border-brand-gold/20 text-brand-ivory"
                      />
                    </div>

                    <div className="flex gap-3 pt-4">
                      <Button
                        variant="outline"
                        className="flex-1"
                        onClick={() => setShowCreateOrderModal(false)}
                      >
                        Cancelar
                      </Button>
                      <Button
                        variant="premium"
                        className="flex-1"
                        onClick={handleCreateOrder}
                        disabled={createOrderMutation.isPending}
                      >
                        {createOrderMutation.isPending ? "Criando..." : "Abrir Comanda"}
                      </Button>
                    </div>
                  </div>
                </div>
              </Card>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* Order Details Modal */}
      <AnimatePresence>
        {selectedOrder && !showAddItemModal && !showCloseOrderModal && !showTransferModal && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/60 backdrop-blur-sm z-40"
              onClick={() => {
                setSelectedOrder(null);
                setSelectedTable(null);
              }}
            />
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="fixed inset-0 flex items-center justify-center z-50 p-4 pointer-events-none"
            >
              <Card className="w-full max-w-2xl bg-brand-ink border-brand-gold/20 max-h-[90vh] overflow-y-auto pointer-events-auto">
                <div className="p-6">
                  <div className="flex items-center justify-between mb-6">
                    <div>
                      <h2 className="text-2xl font-serif text-brand-gold">Comanda #{selectedOrder.orderNumber}</h2>
                      <p className="text-brand-ivory/60 mt-1">
                        {selectedOrder.table ? `Mesa ${selectedOrder.table.number}` : 'Sem mesa'}
                      </p>
                    </div>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => {
                        setSelectedOrder(null);
                        setSelectedTable(null);
                      }}
                    >
                      <X className="w-5 h-5" />
                    </Button>
                  </div>

                  {selectedOrder.clientName && (
                    <div className="mb-4 p-3 bg-brand-smoke/30 rounded-lg border border-brand-gold/10">
                      <div className="flex items-center gap-2">
                        <Users className="w-4 h-4 text-brand-gold" />
                        <span className="text-brand-ivory font-medium">{selectedOrder.clientName}</span>
                      </div>
                    </div>
                  )}

                  <div className="space-y-2 mb-6">
                    <h3 className="text-lg font-semibold text-brand-gold mb-3">Itens da Comanda</h3>
                    {selectedOrder.items && selectedOrder.items.length > 0 ? (
                      selectedOrder.items.map((item) => (
                        <div
                          key={item.id}
                          className="flex items-center justify-between p-3 bg-brand-ink rounded-lg border border-brand-gold/10"
                        >
                          <div className="flex-1">
                            <p className="font-medium text-brand-ivory">{item.productName}</p>
                            <p className="text-sm text-brand-ivory/60">
                              {item.quantity}x R$ {item.price.toFixed(2)}
                            </p>
                            {item.notes && (
                              <p className="text-xs text-brand-ivory/50 mt-1">{item.notes}</p>
                            )}
                          </div>
                          <div className="flex items-center gap-3">
                            <span className="font-bold text-brand-gold">
                              R$ {item.subtotal.toFixed(2)}
                            </span>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => removeItemMutation.mutate({
                                orderId: selectedOrder.id,
                                itemId: item.id,
                              })}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      ))
                    ) : (
                      <p className="text-center text-brand-ivory/60 py-4">Nenhum item adicionado</p>
                    )}
                  </div>

                  <div className="border-t border-brand-gold/20 pt-4 mb-6">
                    <div className="flex justify-between text-lg">
                      <span className="font-medium text-brand-ivory">Subtotal:</span>
                      <span className="font-bold text-brand-gold">
                        R$ {selectedOrder.subtotal.toFixed(2)}
                      </span>
                    </div>
                  </div>

                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      className="flex-1"
                      onClick={() => setShowAddItemModal(true)}
                    >
                      <Plus className="w-4 h-4 mr-2" />
                      Adicionar Item
                    </Button>
                    {selectedOrder.table && (
                      <Button
                        variant="outline"
                        onClick={() => setShowTransferModal(true)}
                      >
                        <ArrowRightLeft className="w-4 h-4 mr-2" />
                        Transferir
                      </Button>
                    )}
                    <Button
                      variant="premium"
                      onClick={() => setShowCloseOrderModal(true)}
                      disabled={!selectedOrder.items || selectedOrder.items.length === 0}
                    >
                      <Check className="w-4 h-4 mr-2" />
                      Fechar Comanda
                    </Button>
                  </div>
                </div>
              </Card>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* Add Item Modal */}
      <AnimatePresence>
        {showAddItemModal && selectedOrder && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[60]"
              onClick={() => setShowAddItemModal(false)}
            />
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="fixed inset-0 flex items-center justify-center z-[70] p-4 pointer-events-none"
            >
              <Card className="w-full max-w-md bg-brand-ink border-brand-gold/20 pointer-events-auto">
                <div className="p-6">
                  <div className="flex items-center justify-between mb-6">
                    <h2 className="text-2xl font-serif text-brand-gold">Adicionar Item</h2>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => setShowAddItemModal(false)}
                    >
                      <X className="w-5 h-5" />
                    </Button>
                  </div>

                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-brand-ivory/80 mb-2">
                        Produto *
                      </label>
                      <select
                        value={selectedProduct}
                        onChange={(e) => setSelectedProduct(e.target.value)}
                        className="w-full px-3 py-2 bg-brand-ink border border-brand-gold/20 rounded-md text-brand-ivory placeholder:text-brand-ivory/40 focus:outline-none focus:ring-2 focus:ring-brand-gold/50 focus:border-brand-gold"
                      >
                        <option value="">Selecione um produto</option>
                        {products?.map((product: Product) => (
                          <option key={product.id} value={product.id}>
                            {product.name} - R$ {product.price?.toFixed(2)}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-brand-ivory/80 mb-2">
                        Quantidade *
                      </label>
                      <Input
                        type="number"
                        value={quantity}
                        onChange={(e) => setQuantity(parseInt(e.target.value) || 1)}
                        min="1"
                        className="bg-brand-ink border-brand-gold/20 text-brand-ivory"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-brand-ivory/80 mb-2">
                        Observações
                      </label>
                      <textarea
                        value={itemNotes}
                        onChange={(e) => setItemNotes(e.target.value)}
                        rows={2}
                        className="w-full px-3 py-2 bg-brand-ink border border-brand-gold/20 rounded-md text-brand-ivory placeholder:text-brand-ivory/40 focus:outline-none focus:ring-2 focus:ring-brand-gold/50 focus:border-brand-gold resize-none"
                        placeholder="Ex: Sem cebola, ponto da carne..."
                      />
                    </div>

                    <div className="flex gap-3 pt-4">
                      <Button
                        variant="outline"
                        className="flex-1"
                        onClick={() => setShowAddItemModal(false)}
                      >
                        Cancelar
                      </Button>
                      <Button
                        variant="premium"
                        className="flex-1"
                        onClick={handleAddItem}
                        disabled={!selectedProduct || addItemMutation.isPending}
                      >
                        {addItemMutation.isPending ? "Adicionando..." : "Adicionar"}
                      </Button>
                    </div>
                  </div>
                </div>
              </Card>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* Close Order Modal */}
      <AnimatePresence>
        {showCloseOrderModal && selectedOrder && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[60]"
              onClick={() => setShowCloseOrderModal(false)}
            />
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="fixed inset-0 flex items-center justify-center z-[70] p-4 pointer-events-none"
            >
              <Card className="w-full max-w-md bg-brand-ink border-brand-gold/20 pointer-events-auto">
                <div className="p-6">
                  <div className="flex items-center justify-between mb-6">
                    <h2 className="text-2xl font-serif text-brand-gold">Fechar Comanda</h2>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => setShowCloseOrderModal(false)}
                    >
                      <X className="w-5 h-5" />
                    </Button>
                  </div>

                  <div className="space-y-4">
                    <div className="bg-brand-ink p-4 rounded-lg space-y-2 border border-brand-gold/10">
                      <div className="flex justify-between text-brand-ivory/60">
                        <span>Subtotal:</span>
                        <span>R$ {selectedOrder.subtotal.toFixed(2)}</span>
                      </div>
                      <div className="flex justify-between text-brand-ivory/60">
                        <span>Desconto:</span>
                        <span className="text-red-400">- R$ {discount.toFixed(2)}</span>
                      </div>
                      <div className="flex justify-between text-brand-ivory/60">
                        <span>Taxa de serviço:</span>
                        <span className="text-emerald-400">+ R$ {serviceCharge.toFixed(2)}</span>
                      </div>
                      <div className="border-t border-brand-gold/20 pt-2 flex justify-between">
                        <span className="text-brand-gold font-bold">Total:</span>
                        <span className="text-brand-gold font-bold text-xl">
                          R$ {(selectedOrder.subtotal - discount + serviceCharge).toFixed(2)}
                        </span>
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-brand-ivory/80 mb-2">
                        Desconto (R$)
                      </label>
                      <Input
                        type="number"
                        value={discount}
                        onChange={(e) => setDiscount(parseFloat(e.target.value) || 0)}
                        min="0"
                        step="0.01"
                        className="bg-brand-ink border-brand-gold/20 text-brand-ivory"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-brand-ivory/80 mb-2">
                        Taxa de serviço (R$)
                      </label>
                      <Input
                        type="number"
                        value={serviceCharge}
                        onChange={(e) => setServiceCharge(parseFloat(e.target.value) || 0)}
                        min="0"
                        step="0.01"
                        className="bg-brand-ink border-brand-gold/20 text-brand-ivory"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-brand-ivory/80 mb-2">
                        Forma de pagamento *
                      </label>
                      <select
                        value={paymentMethod}
                        onChange={(e) => setPaymentMethod(e.target.value)}
                        className="w-full px-3 py-2 bg-brand-ink border border-brand-gold/20 rounded-md text-brand-ivory focus:outline-none focus:ring-2 focus:ring-brand-gold/50 focus:border-brand-gold"
                      >
                        <option value="dinheiro">Dinheiro</option>
                        <option value="cartao_credito">Cartão de Crédito</option>
                        <option value="cartao_debito">Cartão de Débito</option>
                        <option value="pix">PIX</option>
                      </select>
                    </div>

                    <div className="flex gap-3 pt-4">
                      <Button
                        variant="outline"
                        className="flex-1"
                        onClick={() => setShowCloseOrderModal(false)}
                      >
                        Cancelar
                      </Button>
                      <Button
                        variant="premium"
                        className="flex-1"
                        onClick={handleCloseOrder}
                        disabled={closeOrderMutation.isPending}
                      >
                        {closeOrderMutation.isPending ? "Fechando..." : "Fechar Comanda"}
                      </Button>
                    </div>
                  </div>
                </div>
              </Card>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* Transfer Modal */}
      <AnimatePresence>
        {showTransferModal && selectedOrder && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[60]"
              onClick={() => setShowTransferModal(false)}
            />
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="fixed inset-0 flex items-center justify-center z-[70] p-4 pointer-events-none"
            >
              <Card className="w-full max-w-md bg-brand-ink border-brand-gold/20 pointer-events-auto">
                <div className="p-6">
                  <div className="flex items-center justify-between mb-6">
                    <h2 className="text-2xl font-serif text-brand-gold">Transferir Comanda</h2>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => setShowTransferModal(false)}
                    >
                      <X className="w-5 h-5" />
                    </Button>
                  </div>

                  <div className="space-y-4">
                    <p className="text-brand-ivory/60">
                      Mesa atual: <span className="text-brand-gold font-semibold">
                        {selectedOrder.table ? selectedOrder.table.number : 'Sem mesa'}
                      </span>
                    </p>

                    <div>
                      <label className="block text-sm font-medium text-brand-ivory/80 mb-2">Nova Mesa *</label>
                      <select
                        value={transferTableId}
                        onChange={(e) => setTransferTableId(e.target.value)}
                        className="w-full px-3 py-2 bg-brand-ink border border-brand-gold/20 rounded-md text-brand-ivory focus:outline-none focus:ring-2 focus:ring-brand-gold/50 focus:border-brand-gold"
                      >
                        <option value="">Selecione uma mesa</option>
                        {tables
                          ?.filter((t: Table) => t.id !== selectedOrder.tableId && !getTableOrder(t.id))
                          .map((table: Table) => (
                            <option key={table.id} value={table.id}>
                              Mesa {table.number}
                            </option>
                          ))}
                      </select>
                    </div>

                    <div className="flex gap-3 pt-4">
                      <Button
                        variant="outline"
                        className="flex-1"
                        onClick={() => setShowTransferModal(false)}
                      >
                        Cancelar
                      </Button>
                      <Button
                        variant="premium"
                        className="flex-1"
                        onClick={handleTransferOrder}
                        disabled={!transferTableId || transferOrderMutation.isPending}
                      >
                        {transferOrderMutation.isPending ? "Transferindo..." : "Transferir"}
                      </Button>
                    </div>
                  </div>
                </div>
              </Card>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  );
}
